export const TABLE_ID = 'org-orders';

/**
 * Statuses the pending banner counts and filters to — loads sitting on an
 * approve/reject decision. Mirrors `pendingOrderItemCount` in the users route;
 * the two need to stay in step or the banner will disagree with its own count.
 */
export const PENDING_REVIEW_STATUSES = ['PENDING'];

/**
 * The banner steps aside once the table is already showing those loads.
 * "Already showing" means every selected status is a review status — a filter
 * of PENDING plus DELIVERED is not a review view.
 */
export const isReviewingPending = (status: string[]): boolean =>
  status.length > 0 && status.every((s) => PENDING_REVIEW_STATUSES.includes(s));

export type OrgOrderFilters = {
  salesRep: string[];
  search: string;
  status: string[];
  productId: string[];
  locationId: string[];
  deliveryState: string[];
  pickupState: string[];
  pickupCity: string[];
  truckNumber: string[];
  driverName: string[];
  carrierId: string[];
  orderDateFrom: string;
  orderDateTo: string;
  pickupDateFrom: string;
  pickupDateTo: string;
  deliveryDateFrom: string;
  deliveryDateTo: string;
  pageSize: number;
  tag: string[];
  loadOption: string[];
};

/**
 * A saved filter back into filter state.
 *
 * Every field is defaulted, because a filter saved before a field existed
 * simply has no key for it — and applying `undefined` to a multi-select would
 * make it uncontrolled rather than empty.
 */
/**
 * Filters saved by the old Assignment dropdown carry `assignment: ALL | ME |
 * UNASSIGNED` and no `salesRep`. ALL becomes an empty selection; the other two
 * carry across as sentinels the server still understands.
 */
export const migrateAssignment = (assignment: unknown): string[] => {
  if (assignment === 'UNASSIGNED') return ['UNASSIGNED'];
  if (assignment === 'ME') return ['ME'];

  return [];
};

export const readSavedFilters = (saved: any): OrgOrderFilters => ({
  salesRep: saved?.filters?.salesRep ?? migrateAssignment(saved?.filters?.assignment),
  search: saved?.filters?.search ?? '',
  status: saved?.filters?.status ?? [],
  productId: saved?.filters?.productId ?? [],
  locationId: saved?.filters?.locationId ?? [],
  deliveryState: saved?.filters?.deliveryState ?? [],
  pickupState: saved?.filters?.pickupState ?? [],
  pickupCity: saved?.filters?.pickupCity ?? [],
  truckNumber: saved?.filters?.truckNumber ?? [],
  driverName: saved?.filters?.driverName ?? [],
  carrierId: saved?.filters?.carrierId ?? [],
  orderDateFrom: saved?.filters?.orderDateFrom ?? '',
  orderDateTo: saved?.filters?.orderDateTo ?? '',
  pickupDateFrom: saved?.filters?.pickupDateFrom ?? '',
  pickupDateTo: saved?.filters?.pickupDateTo ?? '',
  deliveryDateFrom: saved?.filters?.deliveryDateFrom ?? '',
  deliveryDateTo: saved?.filters?.deliveryDateTo ?? '',
  pageSize: saved?.filters?.pageSize ?? 20,
  tag: saved?.filters?.tag ?? [],
  loadOption: saved?.filters?.loadOption ?? [],
});

/**
 * A stable, order-independent string for comparing two filter sets. Excludes
 * pageSize — that's a view preference, not a filter — so changing page size
 * doesn't look like you've diverged from a saved filter.
 */
const canonicalizeFilters = (f: OrgOrderFilters): string => {
  const sorted = (xs: string[]) => [...xs].sort();
  return JSON.stringify({
    salesRep: sorted(f.salesRep),
    search: f.search,
    status: sorted(f.status),
    productId: sorted(f.productId),
    locationId: sorted(f.locationId),
    deliveryState: sorted(f.deliveryState),
    pickupState: sorted(f.pickupState),
    pickupCity: sorted(f.pickupCity),
    truckNumber: sorted(f.truckNumber),
    driverName: sorted(f.driverName),
    carrierId: sorted(f.carrierId),
    orderDateFrom: f.orderDateFrom,
    orderDateTo: f.orderDateTo,
    pickupDateFrom: f.pickupDateFrom,
    pickupDateTo: f.pickupDateTo,
    deliveryDateFrom: f.deliveryDateFrom,
    deliveryDateTo: f.deliveryDateTo,
    tag: sorted(f.tag),
    loadOption: sorted(f.loadOption),
  });
};

/**
 * The id of the saved filter whose filters exactly match the current ones, or
 * '' when none do. Derived (not stored), so it clears itself the moment any
 * filter changes — including a reset to defaults on navigation.
 */
export const findMatchingSavedFilterId = (
  current: Partial<OrgOrderFilters>,
  saved: Array<{ id: string; filters?: unknown }>
): string => {
  const target = canonicalizeFilters(readSavedFilters({ filters: current }));
  const match = saved.find((s) => canonicalizeFilters(readSavedFilters(s)) === target);
  return match?.id ?? '';
};

/**
 * The status list for this page's filter dropdown.
 *
 * Deliberately its own list rather than the shared `ORG_ORDER_STATUS_OPTIONS`:
 * that one still carries `SHIPPED` and `CANCELLED`, which are not in the Prisma
 * enum, so filtering by either sends an invalid value to the server.
 */
export const ORG_ORDERS_STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'WAITLIST', label: 'Waitlist' },
  { value: 'APPROVED', label: 'Accepted' },
  { value: 'ASSIGNED', label: 'Assigned' },
  { value: 'READY_FOR_PICKUP', label: 'Ready for Pickup' },
  { value: 'ENROUTE', label: 'Enroute' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'INVOICED', label: 'Invoiced' },
  { value: 'CUSTOMER_CANCELLED', label: 'Cancelled' },
  { value: 'PREPAY_REQUIRED', label: 'Prepay Required' },
  { value: 'PICKED_UP', label: 'Picked Up' },
  { value: 'ON_HOLD', label: 'Frozen' },
  { value: 'INVENTORY', label: 'Inventory' },
];
